// @src/utils/seo-utils/seoClgListUtils.ts

import { capitalizeLocationName } from "@/utils/helpers/capitalizeLetter";

const baseUrl = process.env.NEXT_PUBLIC_DOMAIN_URL?.replace(/\/$/, "");
export interface SlugInfo {
  type: "country" | "state" | "city";
  city_name?: string;
  city_url?: string;
  state_name?: string;
  state_url?: string;
  country_name?: string;
  country_url?: string;
  categoryFilterValue?: string;
  courseName?: string;
  categoryType?: string;
  courseUrl?: string;
  specializationName?: string;
  specializationUrl?: string;
}

export interface SEOData {
  page_title: string;
  page_description: string;
  page_content_description: string;
  page_canonical_url: string;
  page_title_content: string;
  keywords: string;
}
export interface SeoDynamicData {
  page_title: string;
  page_description: string;
  keywords: string;
}

export const generateClgListSEOData = (
  slugInfo: SlugInfo,
  collegesParam: string,
  seoData: SeoDynamicData
): SEOData => {
  const formatCategory = (category?: string) =>
    category
      ?.replace(/-/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase()) ?? "";

  const getLocationDetails = (
    collegesParam: string
  ): { locationName: string; pageCanonicalUrl: string } => {
    const trimmedParam = collegesParam?.trim();

    const match = trimmedParam.match(/colleges-in-(.+)/);
    const locationName = match ? match[1].replace(/-/g, " ") : "";
    const pageCanonicalUrl = match ? `colleges-in-${match[1]}` : "";

    return { locationName, pageCanonicalUrl };
  };

  let categoryVal = "";

  // Case 1: If both `specializationName` and `courseName` exist → "B-Tech in Computer Science"
  if (slugInfo?.specializationName && slugInfo?.courseName) {
    categoryVal = `${slugInfo.courseName} in ${slugInfo.specializationName}`;
  }

  // Case 2: If it's a `course` category, use `courseName` OR fallback to `categoryFilterValue`
  else if (slugInfo?.categoryType === "course") {
    categoryVal = slugInfo?.courseName
      ? slugInfo.courseName
      : formatCategory(slugInfo?.categoryFilterValue);
  }

  // Case 3: If it's neither of the above, just use `categoryFilterValue`
  else {
    categoryVal = formatCategory(slugInfo?.categoryFilterValue);
  }

  // const { locationName } = getLocationDetails(slugInfo, collegesParam);
  const { locationName } = getLocationDetails(collegesParam);

  const capitalizeLocation = capitalizeLocationName(locationName);
  // if collegesParam includess iit-colleges-in-, nit-colleges-in-, or government-colleges-in- then categoryText must be iit, nit, or government
  const specialMatch = collegesParam.match(
    /(iit|nit|government)-colleges-in-/i
  );

  let categoryText;

  if (specialMatch) {
    categoryText =
      specialMatch[1] === "government"
        ? String(specialMatch[1]).charAt(0).toUpperCase() +
          String(specialMatch[1]).slice(1) +
          " "
        : specialMatch[1].toUpperCase() + " ";
  } else {
    categoryText = categoryVal ? `${categoryVal} ` : "";
  }

  const baseKeywords = [
    `best ${categoryText}colleges in ${locationName.toLowerCase()}`,
    `top ${categoryText}colleges in ${locationName.toLowerCase()}`,
    `top 10 ${categoryText}colleges in ${locationName.toLowerCase()}`,
    `tier 1 ${categoryText}colleges in ${locationName.toLowerCase()}`,
    `college rankings`,
    `top 50 universities in ${locationName.toLowerCase()}`,
  ].join(", ");

  const page_title = slugInfo?.specializationName
    ? `Top ${categoryText}Colleges in ${capitalizeLocation} 2025`
    : `Top ${categoryText}Colleges in ${capitalizeLocation} 2025: Ranking, Fees, Courses`;

  return {
    page_title: seoData?.page_title ? seoData?.page_title : `${page_title}`,
    page_title_content: page_title,
    page_description: seoData?.page_description
      ? seoData?.page_description
      : `Discover the best ${categoryText}colleges in ${capitalizeLocation} with our detailed college rankings. Explore top 10 ${categoryText}colleges in ${capitalizeLocation}, tier 1 ${categoryText}colleges, and top 50 universities.`,
    page_content_description: `Discover the **Best ${categoryText}Colleges in ${capitalizeLocation}**, including **tier 1 ${categoryText} in ${capitalizeLocation}** with exceptional rankings. Explore the **Top 10 ${categoryText}Colleges in ${capitalizeLocation}** and **Top 10 Universities in ${capitalizeLocation}** known for academic excellence with **best placement ${categoryText}colleges in ${capitalizeLocation}** to shape your future with confidence.`,
    page_canonical_url: `${baseUrl}/${collegesParam}`,
    keywords: seoData?.keywords ? seoData?.keywords : baseKeywords,
  };
};
